const DEFAULT_DESIGN = {
    palette: {
        primary: "#21416b",
        secondary: "#707ea7",
        title: "#17324f",
        text: "#1f2937",
        muted: "#64748b",
    },
    typography: {
        titleFontFamily: "Georgia, serif",
        bodyFontFamily: "'Lato', sans-serif",
        titleSize: 46,
        recipientSize: 34,
        bodySize: 18,
    },
    frame: {
        color: "#21416b",
        width: 8,
        radius: 28,
    },
    layout: {
        contentAlign: "center",
        paddingX: 84,
        paddingY: 72,
    },
    blocks: {
        eyebrow: "Certification officielle",
        intro: "Ce certificat est décerné à",
        footerNote: "Référence module : {{module.reference}} | Date : {{generic.today}}",
        signatureLabel: "Signature",
    },
};

export const createEmptyCertificatForm = () => ({
    id: null,
    nom: "",
    reference: "",
    versionGroupCode: "",
    description: "Pour avoir complété avec succès le module {{module.name}}.",
    signature: "Direction Formation",
    templateHtml: "",
    orientation: "PORTRAIT",
    dimension: "A4",
    categorie: "FORMATION",
    taille: "STANDARD",
    isActive: true,
    publicationStatus: "DRAFT",
    archived: false,
    versionNumber: 1,
    currentVersion: true,
    attachmentFile: "",
    designConfig: JSON.parse(JSON.stringify(DEFAULT_DESIGN)),
});

export const normalizeCertificatForForm = (certificat) => ({
    ...createEmptyCertificatForm(),
    ...certificat,
    versionGroupCode: certificat?.versionGroupCode || certificat?.reference || "",
    designConfig: {
        ...DEFAULT_DESIGN,
        ...(certificat?.designConfig || {}),
        palette: { ...DEFAULT_DESIGN.palette, ...(certificat?.designConfig?.palette || {}) },
        typography: { ...DEFAULT_DESIGN.typography, ...(certificat?.designConfig?.typography || {}) },
        frame: { ...DEFAULT_DESIGN.frame, ...(certificat?.designConfig?.frame || {}) },
        layout: { ...DEFAULT_DESIGN.layout, ...(certificat?.designConfig?.layout || {}) },
        blocks: { ...DEFAULT_DESIGN.blocks, ...(certificat?.designConfig?.blocks || {}) },
    },
});

const escapeHtml = (value = "") =>
    String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;");

const toHtmlLines = (value = "") => escapeHtml(value).replace(/\r?\n/g, "<br/>");

export const buildCertificatTemplateHtml = (form) => {
    const design = normalizeCertificatForForm(form).designConfig;
    const primary = design.palette.primary;
    const secondary = design.palette.secondary;
    const titleColor = design.palette.title;
    const textColor = design.palette.text;
    const mutedColor = design.palette.muted;
    const titleFont = design.typography.titleFontFamily;
    const bodyFont = design.typography.bodyFontFamily;
    const titleSize = design.typography.titleSize;
    const recipientSize = design.typography.recipientSize;
    const bodySize = design.typography.bodySize;
    const frameWidth = design.frame.width;
    const frameRadius = design.frame.radius;
    const frameColor = design.frame.color;
    const paddingX = design.layout.paddingX;
    const paddingY = design.layout.paddingY;
    const align = design.layout.contentAlign;

    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8"/>
  <style>
    @page { size: ${form.dimension || "A4"} ${(form.orientation || "PORTRAIT") === "LANDSCAPE" ? "landscape" : "portrait"}; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; background: #eef2f7; font-family: ${bodyFont}; color: ${textColor}; }
    .page { min-height: 100vh; padding: 38px; display: flex; align-items: center; justify-content: center; }
    .cert { width: 100%; min-height: 1020px; position: relative; overflow: hidden; border: ${frameWidth}px solid ${frameColor}; border-radius: ${frameRadius}px; padding: ${paddingY}px ${paddingX}px; background: linear-gradient(180deg, rgba(255,255,255,.96), rgba(248,250,252,.96)); }
    .cert:before { content: ""; position: absolute; inset: 18px; border: 1px solid rgba(163,176,210,.35); border-radius: calc(${frameRadius}px - 8px); pointer-events: none; }
    .eyebrow { position: relative; z-index: 1; font-size: 14px; text-transform: uppercase; letter-spacing: .24em; color: ${secondary}; text-align: ${align}; margin-bottom: 20px; }
    .title { position: relative; z-index: 1; font-family: ${titleFont}; font-size: ${titleSize}px; font-weight: 700; color: ${titleColor}; text-align: ${align}; margin: 0 0 20px; line-height: 1.15; }
    .intro { position: relative; z-index: 1; font-size: 20px; color: ${mutedColor}; text-align: ${align}; margin-bottom: 26px; }
    .recipient { position: relative; z-index: 1; font-family: ${titleFont}; font-size: ${recipientSize}px; font-weight: 700; color: ${titleColor}; text-align: ${align}; margin-bottom: 28px; text-decoration: underline; text-decoration-color: ${secondary}; text-underline-offset: 12px; }
    .body { position: relative; z-index: 1; max-width: 920px; margin: 0 auto 42px; font-size: ${bodySize}px; line-height: 1.8; text-align: ${align}; color: ${textColor}; }
    .chips { position: relative; z-index: 1; display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; margin-bottom: 44px; }
    .chip { padding: 8px 14px; border-radius: 999px; background: rgba(33,65,107,.08); color: ${primary}; border: 1px solid rgba(33,65,107,.14); font-size: 13px; }
    .footer { position: relative; z-index: 1; display: flex; justify-content: space-between; align-items: flex-end; gap: 32px; }
    .note { flex: 1; font-size: 14px; line-height: 1.7; color: ${mutedColor}; }
    .signature { min-width: 280px; text-align: center; }
    .signature-line { height: 1px; background: rgba(31,41,55,.35); margin-bottom: 12px; }
    .signature-label { font-size: 13px; text-transform: uppercase; letter-spacing: .12em; color: ${mutedColor}; margin-bottom: 8px; }
    .signature-name { font-size: 20px; font-weight: 600; color: ${textColor}; }
  </style>
</head>
<body>
  <div class="page">
    <div class="cert" {{design.backgroundStyle}}>
      <div class="eyebrow">${toHtmlLines(design.blocks.eyebrow)}</div>
      <h1 class="title">{{certificat.name}}</h1>
      <div class="intro">${toHtmlLines(design.blocks.intro)}</div>
      <div class="recipient">{{user.fullName}}</div>
      <div class="body">${toHtmlLines(form.description || "")}</div>
      <div class="chips">
        <div class="chip">{{module.name}}</div>
        <div class="chip">{{formation.startDate}}</div>
        <div class="chip">{{formation.location}}</div>
      </div>
      <div class="footer">
        <div class="note">${toHtmlLines(design.blocks.footerNote)}</div>
        <div class="signature">
          <div class="signature-line"></div>
          <div class="signature-label">${toHtmlLines(design.blocks.signatureLabel)}</div>
          <div class="signature-name">{{certificat.signature}}</div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
};

export const buildPreviewHtml = (form, sampleValues = {}, backgroundUrl = "") => {
    const normalized = normalizeCertificatForForm(form);
    const template = normalized.templateHtml?.trim() || buildCertificatTemplateHtml(normalized);
    const replacementMap = {
        "{{certificat.name}}": normalized.nom || sampleValues["{{certificat.name}}"] || "",
        "{{certificat.reference}}": normalized.reference || sampleValues["{{certificat.reference}}"] || "",
        "{{certificat.description}}": normalized.description || sampleValues["{{certificat.description}}"] || "",
        "{{certificat.signature}}": normalized.signature || sampleValues["{{certificat.signature}}"] || "",
        ...sampleValues,
    };

    let html = template.replace(
        "{{design.backgroundStyle}}",
        backgroundUrl
            ? `style="background-image:url('${backgroundUrl}');background-size:cover;background-position:center;"`
            : ""
    );

    Object.entries(replacementMap).forEach(([token, value]) => {
        html = html.replaceAll(token, escapeHtml(value || ""));
    });

    return html;
};
